import React from 'react';
import * as RGL_UTILS from 'react-grid-layout/build/utils';
import {
  sortLayoutItems,
  getLayoutItem,
  cloneLayoutItem,
  validateLayout,
  correctBounds,
  getAllCollisions,
  bottom,
} from 'react-grid-layout/build/utils';

// static grid order
// https://github.com/react-grid-layout/react-grid-layout/issues/1100#issuecomment-583741129
// https://stackoverflow.com/questions/51984849/how-to-implement-static-sized-grid-like-3x3-in-react-grid-layout

// Possible issue with touch devices?? not confirmed
// rowHeight: ?number = 150,
// onDragStart = () => {
//   // This disables default scrolls on touch screens when resizing or moving boxes
//   document.ontouchmove = function(e){ e.preventDefault(); }
// };

// onDragStop = () => {
//   // This reactivates default scrolling behavior on touch screens after resizing or moving boxes
//   document.ontouchmove = function(){ return true; }
// };

// alternatives:
// https://github.com/gridstack/gridstack.js
// https://codesandbox.io/s/vibvy?file=/src/components/Grid.js

function compact(layout, compactType, cols) {
  // We go through the items by row and column.
  const sorted = sortLayoutItems(layout, 'vertical');
  // Holding for new items.
  const out = Array(layout.length);

  for (let i = 0, len = sorted.length; i < len; i++) {
    let l = cloneLayoutItem(sorted[i]);

    // Don't move static elements
    if (!l.static) {
      l.y = Math.floor(i / cols);
      l.x = i % cols;
    }
    // Add to output array to make sure they still come out in the right order.
    out[i] = l;
    // Clear moved flag, if it exists.
    l.moved = false;
  }
  return out;
}
RGL_UTILS.compact = compact;

/* No code is added here. It's just copied from library otherwise grid wont call our overrided compact method */
RGL_UTILS.synchronizeLayoutWithChildren = function synchronizeLayoutWithChildren(
  initialLayout,
  children,
  cols,
  compactType
) {
  initialLayout = initialLayout || []; // Generate one layout item per child.

  var layout =
    /*: LayoutItem[]*/
    [];

  React.Children.forEach(children, function (
    child,
    /*: ReactElement<any>*/
    i
    /*: number*/
  ) {
    // Don't overwrite if it already exists.
    var exists = getLayoutItem(initialLayout, String(child.key));

    if (exists) {
      layout[i] = cloneLayoutItem(exists);
    } else {
      if (!isProduction && child.props._grid) {
        console.warn(
          '`_grid` properties on children have been deprecated as of React 15.2. ' + // eslint-disable-line
            'Please use `data-grid` or add your properties directly to the `layout`.'
        );
      }

      var g = child.props['data-grid'] || child.props._grid; // Hey, this item has a data-grid property, use it.

      if (g) {
        if (!isProduction) {
          validateLayout([g], 'ReactGridLayout.children');
        } // FIXME clone not really necessary here

        layout[i] = cloneLayoutItem(
          _objectSpread(
            _objectSpread({}, g),
            {},
            {
              i: child.key,
            }
          )
        );
      } else {
        // Nothing provided: ensure this is added to the bottom
        // FIXME clone not really necessary here
        layout[i] = cloneLayoutItem({
          w: 1,
          h: 1,
          x: 0,
          y: bottom(layout),
          i: String(child.key),
        });
      }
    }
  }); // Correct the layout.

  var correctedLayout = correctBounds(layout, {
    cols: cols,
  });
  return compact(correctedLayout, compactType, cols);
};

/* All code is re-used from the original method except the 'isLeftShift' related code. */
function moveElement(
  layout,
  l,
  x,
  y,
  isUserAction,
  preventCollision,
  compactType,
  cols,
  isLeftShift // overriden - nitesh
) {
  // If this is static and not explicitly enabled as draggable,
  // no move is possible, so we can short-circuit this immediately.
  if (l.static && l.isDraggable !== true) return layout;

  // Short-circuit if nothing to do.
  if (l.y === y && l.x === x) return layout;

  // console.log(
  //   `Moving element ${l.i} to [${String(x)},${String(y)}] from [${l.x},${l.y}]`
  // );
  const oldX = l.x;
  const oldY = l.y;

  // This is quite a bit faster than extending the object
  if (typeof x === 'number') l.x = x;
  if (typeof y === 'number') l.y = y;
  l.moved = true;

  // If this collides with anything, move it.
  // When doing this comparison, we have to sort the items we compare with
  // to ensure, in the case of multiple collisions, that we're getting the
  // nearest collision.
  let sorted = sortLayoutItems(layout, compactType);
  const movingUp =
    compactType === 'vertical' && typeof y === 'number'
      ? oldY >= y
      : compactType === 'horizontal' && typeof x === 'number'
      ? oldX >= x
      : false;
  if (movingUp) sorted = sorted.reverse();
  const collisions = getAllCollisions(sorted, l);

  // There was a collision; abort
  if (preventCollision && collisions.length) {
    // console.log(`Collision prevented on ${l.i}, reverting.`);
    l.x = oldX;
    l.y = oldY;
    l.moved = false;
    return layout;
  }
  // overriden - nitesh
  if (isUserAction) {
    isUserAction = false;
    if (oldX === x) isLeftShift = oldY - y <= 0;
    if (oldY === y) isLeftShift = oldX - x <= 0;
  }
  // Move each item that collides away from this element.
  for (let i = 0, len = collisions.length; i < len; i++) {
    const collision = collisions[i];
    // console.log(
    //   `Resolving collision between ${l.i} at [${l.x},${l.y}] and ${collision.i} at [${collision.x},${collision.y}]`
    // );

    // Short circuit so we can't infinite loop
    if (collision.moved) continue;

    // Don't move static items - we have to move *this* element away
    if (collision.static) {
      layout = RGL_UTILS.moveElementAwayFromCollision(
        layout,
        collision,
        l,
        isUserAction,
        compactType,
        cols,
        isLeftShift // overriden - nitesh
      );
    } else {
      layout = RGL_UTILS.moveElementAwayFromCollision(
        layout,
        l,
        collision,
        isUserAction,
        compactType,
        cols,
        isLeftShift // overriden - nitesh
      );
    }
  }

  return layout;
}

RGL_UTILS.moveElement = moveElement;

RGL_UTILS.moveElementAwayFromCollision = function moveElementAwayFromCollision(
  layout,
  collidesWith,
  itemToMove,
  isUserAction,
  compactType,
  cols,
  isLeftShift
) {
  const compactH = compactType === 'horizontal';

  const preventCollision = collidesWith.static; // we're already colliding (not for static items)
  const isTileWrapping = isLeftShift
    ? itemToMove.x - 1 < 0
    : itemToMove.x + 1 >= cols;
  const deltaShift = isLeftShift ? -1 : 1;
  const x = isTileWrapping
    ? isLeftShift
      ? cols - 1
      : 0
    : itemToMove.x + deltaShift;
  const y = isTileWrapping ? itemToMove.y + deltaShift : itemToMove.y;

  return moveElement(
    layout,
    itemToMove,
    compactH ? x : undefined,
    y,
    isUserAction,
    preventCollision,
    compactType,
    cols,
    isLeftShift
  );
};
